import AlertCircleIcon from '@assets/icons/alert.svg';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import React, { useEffect } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_SPRING_CONFIG_INTRO } from '@experiences/blocs/utils/animations';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const BANNER_HEIGHT = 32;

interface SalesPortalBannerProps {
  message: string;
}

/**
 * Full-width banner, animates down from above the top bar on mount.
 */
export const SalesPortalBanner: React.FC<SalesPortalBannerProps> = ({
  message,
}) => {
  const [styles, theme] = useStyles();
  const translateY = useSharedValue(-BANNER_HEIGHT);

  useEffect(() => {
    translateY.value = withSpring(0, {
      ...DEFAULT_SPRING_CONFIG_INTRO,
      velocity: 0,
    });
  }, [translateY]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  return (
    <Animated.View
      accessibilityRole="alert"
      style={[styles.container, animatedStyle]}
    >
      <View style={styles.content}>
        <AlertCircleIcon
          aria-hidden
          fill={theme.sk.color.icon.negative.primary}
          height={16}
          width={16}
        />
        <Typography
          color={theme.sk.color.text.error}
          numberOfLines={1}
          variant="paragraph-md-600"
        >
          {message}
        </Typography>
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.error,
    height: BANNER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    width: '100%',
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'center',
  },
}));
